/* eslint-disable */
import axios from "axios";
import store from "@/store";
import customModal from "../customModal/customModal.vue";




const createDefaultDateTime = () => {
  const now = new Date();
  const localDate = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 16);
};

const generateUuid = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const random = Math.random() * 16 | 0;
    const value = char === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

const DEFAULT_DOSAGE_UNITS = [
  "mg",
  "mcg",
  "g",
  "kg",
  "ng",
  "mL",
  "L",
  "IU",
  "U",
  "mmol",
  "mol",
  "mg/mL",
  "mcg/mL",
  "mg/kg",
];

const DEFAULT_DOSAGE_ROUTES = [
  "Oral",
  "IV",
  "IM",
  "SC",
  "Topical",
  "Inhalation",
  "Intranasal",
  "Sublingual",
  "Transdermal",
  "Rectal",
];

const createDefaultForm = () => ({
  cohortId: generateUuid(),
  studyId: "",
  cohortCode: "",
  cohortName: "",
  cohortNumber: 0,
  description: "",
  studyPart: "",
  phase: "",
  designType: "",
  dose: {
    amount: 0,
    unit: "",
    route: "",
    frequency: "",
    formulation: "",
  },
  plannedSubjects: 0,
  plannedActive: 0,
  plannedPlacebo: 0,
  sentinelCount: 0,
  soAId: "",
  armMapping: {
    armCode: "",
    armName: "",
    sdtmArm: "",
  },
  status: "",
  sequence: 0,
  isEscalationAllowed: false,
  previousCohortCode: "",
  effectiveFrom: createDefaultDateTime(),
  effectiveTo: createDefaultDateTime(),
  version: 0,
});

export default {
  name: "CreateCohortModal",
  components: {
    customModal,
  },
  props: {
    editingId: {
      type: String,
      default: null,
    },
  },
  emits: ["closeModal", "saved"],
  created() {
    this.isEditMode = !!this.editingId;
    this.loadingDetails = !!this.editingId;
  },
  data() {
    return {
      baseurl: process.env.VUE_APP_Service_URL,
      phases: [],
      soaIds: [],
      loadingSoaIds: false,
      dosageUnits: [...DEFAULT_DOSAGE_UNITS],
      dosageRoutes: [...DEFAULT_DOSAGE_ROUTES],
      submitting: false,
      phaseLoadError: "",
      loadingDetails: false,
      isEditMode: false,
      form: createDefaultForm(),
      errors: {
        cohortCode: "",
        cohortName: "",
      },
    };
  },

  async mounted() {
    if (this.editingId) {
      try {
        await Promise.all([
          this.getSoaIds(),
          this.fetchCohortDetails(),
          this.loadStudyPhases(),
        ]);
      } finally {
        this.loadingDetails = false;
      }
    } else {
      this.form.studyId = this.getCurrentStudyId();
      this.getSoaIds();
      await this.loadStudyPhases();
    }
  },

  methods: {
    clearErrors() {
      this.errors.cohortCode = "";
      this.errors.cohortName = "";
    },

    validate() {
      this.clearErrors();
      let isValid = true;

      if (!this.form.cohortCode || !this.form.cohortCode.trim()) {
        this.errors.cohortCode = "Please enter a cohort code.";
        isValid = false;
      }

      if (!this.form.cohortName || !this.form.cohortName.trim()) {
        this.errors.cohortName = "Please enter a cohort name.";
        isValid = false;
      }

      return isValid;
    },

    normalizeNumber(value) {
      const parsedValue = Number(value);
      return Number.isNaN(parsedValue) ? 0 : parsedValue;
    },

    toIsoString(value) {
      return value ? new Date(value).toISOString() : "";
    },

    toDateTimeLocal(value) {
      if (!value) {
        return "";
      }

      const date = new Date(value);
      const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
      return localDate.toISOString().slice(0, 16);
    },

    getCurrentStudyId() {
      return store.getters.getStudyIs || "";
    },

    ensureCurrentPhaseOption() {
      if (this.form.phase && !this.phases.includes(this.form.phase)) {
        this.phases = [this.form.phase, ...this.phases];
      }
    },

    ensureCurrentUnitOption() {
      if (this.form.dose.unit && !this.dosageUnits.includes(this.form.dose.unit)) {
        this.dosageUnits = [this.form.dose.unit, ...this.dosageUnits];
      }
    },

    ensureCurrentRouteOption() {
      if (this.form.dose.route && !this.dosageRoutes.includes(this.form.dose.route)) {
        this.dosageRoutes = [this.form.dose.route, ...this.dosageRoutes];
      }
    },

    async fetchCohortDetails() {
      const GET_COHORT_URL = `${this.baseurl}/management/cohort/get`;
      const idtoken = store.getters.getIdToken;

      if (!this.editingId) {
        return;
      }

      try {
        const response = await axios.get(`${GET_COHORT_URL}?id=${this.editingId}`, {
          headers: {
            Authorization: "Bearer " + idtoken,
            "Content-Type": "application/json",
          },
        });

        const data = response.data || {};

        this.form = {
          cohortId: data.cohortId || data.id || generateUuid(),
          studyId: this.getCurrentStudyId(),
          soAId: data.soAId || this.form.soAId || "",
          cohortCode: data.cohortCode || "",
          cohortName: data.cohortName || "",
          cohortNumber: this.normalizeNumber(data.cohortNumber),
          description: data.description || "",
          studyPart: data.studyPart || "",
          phase: data.phase || "",
          designType: data.designType || "",
          dose: {
            amount: this.normalizeNumber(data.dose?.amount),
            unit: data.dose?.unit || "",
            route: data.dose?.route || "",
            frequency: data.dose?.frequency || "",
            formulation: data.dose?.formulation || "",
          },
          plannedSubjects: this.normalizeNumber(data.plannedSubjects),
          plannedActive: this.normalizeNumber(data.plannedActive),
          plannedPlacebo: 0,
          sentinelCount: 0,
          armMapping: {
            armCode: data.armMapping?.armCode || "",
            armName: data.armMapping?.armName || "",
            sdtmArm: "",
          },
          status: data.status || "",
          sequence: this.normalizeNumber(data.sequence),
          isEscalationAllowed: !!data.isEscalationAllowed,
          previousCohortCode: data.previousCohortCode || "",
          effectiveFrom: this.toDateTimeLocal(data.effectiveFrom) || createDefaultDateTime(),
          effectiveTo: this.toDateTimeLocal(data.effectiveTo) || createDefaultDateTime(),
          version: this.normalizeNumber(data.version),
        };

        this.ensureCurrentPhaseOption();
        this.ensureCurrentUnitOption();
        this.ensureCurrentRouteOption();
      } catch (err) {
        console.error("Failed to load cohort details:", err);
        alert(
          err.response?.data?.detail ||
          err.response?.data?.message ||
          "Failed to load cohort details."
        );
        this.closeModal();
      }
    },

    async loadStudyPhases() {
      const GET_STUDY_PHASES_URL = `${this.baseurl}/management/studyphase/getstudyphases`;
      const currentStudyId = this.getCurrentStudyId();
      const idtoken = store.getters.getIdToken;

      if (!currentStudyId) {
        this.phases = [];
        this.phaseLoadError = "";
        return;
      }

      try {
        this.phaseLoadError = "";
        const response = await axios.get(
          `${GET_STUDY_PHASES_URL}?studyId=${currentStudyId}`,
          {
            headers: {
              Authorization: "Bearer " + idtoken,
              "Content-Type": "application/json",
            },
          }
        );

        const phaseData = Array.isArray(response.data?.data) ? response.data.data : [];
        this.phases = [...new Set(phaseData.map((item) => item?.name).filter(Boolean))];
        this.ensureCurrentPhaseOption();
      } catch (err) {
        console.error("Failed to load study phases:", err);
        this.phases = [];
        this.phaseLoadError = "Failed to load phases.";
      }
    },

    buildPayload() {
      return {
        cohortId: this.form.cohortId || generateUuid(),
        studyId: this.getCurrentStudyId(),
        cohortCode: this.form.cohortCode.trim(),
        cohortName: this.form.cohortName.trim(),
        cohortNumber: this.normalizeNumber(this.form.cohortNumber),
        description: this.form.description || "",
        studyPart: this.form.studyPart || "",
        phase: this.form.phase || "",
        designType: this.form.designType || "",
        dose: {
          amount: this.normalizeNumber(this.form.dose.amount),
          unit: this.form.dose.unit || "",
          route: this.form.dose.route || "",
          frequency: this.form.dose.frequency || "",
          formulation: this.form.dose.formulation || "",
        },
        plannedSubjects: this.normalizeNumber(this.form.plannedSubjects),
        plannedActive: this.normalizeNumber(this.form.plannedActive),
        plannedPlacebo: 0,
        sentinelCount: 0,
        soAId: this.form.soAId || "",
        armMapping: {
          armCode: this.form.armMapping.armCode || "",
          armName: this.form.armMapping.armName || "",
          sdtmArm: "",
        },
        status: this.form.status || "",
        sequence: this.normalizeNumber(this.form.sequence),
        isEscalationAllowed: !!this.form.isEscalationAllowed,
        previousCohortCode: this.form.previousCohortCode || "",
        effectiveFrom: this.toIsoString(this.form.effectiveFrom),
        effectiveTo: this.toIsoString(this.form.effectiveTo),
        version: this.normalizeNumber(this.form.version),
      };
    },

    async submitCohort() {
      if (this.submitting || !this.validate()) {
        return;
      }

      const idtoken = store.getters.getIdToken;
      const payload = this.buildPayload();
      const CREATE_COHORT_URL = `${this.baseurl}/management/cohort/create`;
      const UPDATE_COHORT_URL = `${this.baseurl}/management/cohort/update`;

      try {
        this.submitting = true;
        if (this.isEditMode && this.editingId) {
          await axios.put(`${UPDATE_COHORT_URL}?id=${this.editingId}`, payload, {
            headers: {
              Authorization: "Bearer " + idtoken,
              "Content-Type": "application/json",
            },
          });
          alert("Cohort updated successfully.");
        } else {
          await axios.post(CREATE_COHORT_URL, payload, {
            headers: {
              Authorization: "Bearer " + idtoken,
              "Content-Type": "application/json",
            },
          });
          alert("Cohort created successfully.");
        }

        this.$emit("saved");
        this.closeModal();
      } catch (err) {
        console.error("Save cohort failed:", err);
        alert(
          err.response?.data?.detail ||
          err.response?.data?.message ||
          "Failed to save cohort."
        );
      } finally {
        this.submitting = false;
      }
    },

    closeModal() {
      this.$emit("closeModal");
    },
    async getSoaIds() {
      const idtoken = store.getters.getIdToken;
      const studyId = this.getCurrentStudyId();

      await axios
        .get(`${this.baseurl}/management/cohort/getsoaids?studyId=${studyId}`, {
          headers: {
            Authorization: "Bearer " + idtoken,
            "Content-Type": "application/json",
          },
        })
        .then((res) => {
          this.soaIds = res.data;
        })
        .catch((err) => {
          console.log(err);
          this.soaIds = [];
        });
    }
  },
};
